import { useEffect } from "react";
import { AccessibilityInfo, Platform, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Button } from "../primitives/Button";
import type { UiTheme } from "../theme";

/** A program has at most this many days (ProgramModel.days). */
export const MAX_PROGRAM_DAYS = 7;

// #368: the diary's selection bar. The count and Done, then Delete and the
// one thing the picked sessions can become: one is planned on a day (Plan),
// two or more are the days of a program (Create program). The phone docks it
// in place of the tab bar; the web puts it on top of the list, in place of
// «New workout». Two rows at most: the order of the picks is on the rows'
// rings, and on the web's detail pane.
export function DiarySelectionBar({
  theme,
  count,
  busy,
  ready,
  deleting,
  onDone,
  onDelete,
  onPlan,
  onCreateProgram,
  docked = false,
  showDone = true,
}: {
  theme: UiTheme;
  count: number;
  /** The picked sessions are being read for Plan or Create program. */
  busy: boolean;
  /** false: the unit the loads are read in is not known yet. */
  ready: boolean;
  deleting: boolean;
  onDone: () => void;
  onDelete: () => void;
  onPlan: () => void;
  onCreateProgram: () => void;
  /** At the bottom of the phone: a rule above, none below. */
  docked?: boolean;
  /** false on the wide web: the header's toggle already says Done. */
  showDone?: boolean;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const tooMany = count > MAX_PROGRAM_DAYS;
  const counted = t("mobile.workouts.selectedCount", { count });
  const tooManyWhy = tooMany ? t("mobile.workouts.selectHintTooMany", { max: MAX_PROGRAM_DAYS }) : null;
  // iOS has no live region: the count is announced.
  useEffect(() => {
    if (Platform.OS === "ios") AccessibilityInfo.announceForAccessibility(counted);
  }, [counted]);
  return (
    <View style={[styles.bar, docked ? styles.docked : styles.top]}>
      <View style={styles.line}>
        <Text style={styles.count} accessibilityLiveRegion="polite" aria-live="polite">
          {counted}
        </Text>
        {showDone ? (
          <Button theme={theme} variant="ghost" compact label={t("editorV3.selectDone")} onPress={onDone} />
        ) : null}
      </View>
      {tooManyWhy ? <Text style={styles.hint}>{tooManyWhy}</Text> : null}
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="danger"
          icon="trash"
          label={t("workout.delete")}
          onPress={onDelete}
          loading={deleting}
          disabled={count === 0 || busy}
          style={styles.flex}
        />
        <Button
          theme={theme}
          icon={count === 1 ? "calendar" : undefined}
          label={count === 1 ? t("mobile.workouts.plan") : t("mobile.workouts.createProgram")}
          numberOfLines={2}
          onPress={count === 1 ? onPlan : onCreateProgram}
          loading={busy}
          disabled={count === 0 || tooMany || deleting || !ready}
          accessibilityHint={tooManyWhy ?? undefined}
          style={styles.flex}
        />
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    bar: {
      backgroundColor: p.surface,
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
      gap: s.s2,
    },
    docked: { borderTopWidth: 1, borderTopColor: p.border },
    top: { paddingTop: s.s3, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: p.border },
    line: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    // Stretch: when one label wraps, both buttons take the taller height.
    actions: { flexDirection: "row", alignItems: "stretch", gap: s.s3 },
    flex: { flex: 1 },
    count: {
      flex: 1,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    hint: { color: p.ink3, fontSize: ty.sizes.sm },
  });
}
